import { useState, useEffect, useCallback } from "react";
import { getOrganization } from "../services/organizationService";

/**
 * Custom hook for fetching organization data
 * @param {Array<string>|null} fields - Specific fields to extract (null = all fields)
 * @returns {Object} { data, loading, error, refresh }
 */
export const useOrganizationData = (fields = null) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Convert fields array to string for stable dependency
  const fieldsKey = fields ? JSON.stringify(fields) : null;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getOrganization();

      if (response.success && response.data) {
        // If specific fields requested, return only those
        if (fieldsKey) {
          const fieldsList = JSON.parse(fieldsKey);
          const filtered = {};
          fieldsList.forEach((field) => {
            filtered[field] = response.data[field];
          });
          setData(filtered);
        } else {
          setData(response.data);
        }
      } else {
        setError(response.message || "Failed to load organization");
      }
    } catch (err) {
      console.error("Error loading organization:", err);
      setError("An error occurred while loading organization");
    } finally {
      setLoading(false);
    }
  }, [fieldsKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh: fetchData };
};
